// Mission & Vision Page

import React from 'react';
import { motion } from 'framer-motion';
import { Eye, Shield, Target, Users, Lightbulb, Heart } from 'lucide-react';

const MissionPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      <div className="container-width section-padding py-20">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl lg:text-6xl font-bold text-gradient-flag mb-6">
            Mission & Vision
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Building a transparent democracy where every citizen has access to information,
            institutions are accountable, and governance serves the people.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="flex items-center space-x-3 mb-6">
              <Target className="w-8 h-8 text-primary-600" />
              <h2 className="text-3xl font-bold text-gray-900">Our Mission</h2>
            </div>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              To empower citizens with tools and knowledge to create transparent institutions,
              hold governments accountable, and build a democracy that truly serves its people
              through community engagement and data-driven advocacy.
            </p>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start space-x-3">
                <Eye className="w-5 h-5 text-primary-600 mt-1 flex-shrink-0" />
                <span>Promote transparency in government operations and decision-making</span>
              </li>
              <li className="flex items-start space-x-3">
                <Shield className="w-5 h-5 text-primary-600 mt-1 flex-shrink-0" />
                <span>Ensure accountability of public institutions and officials</span>
              </li>
              <li className="flex items-start space-x-3">
                <Users className="w-5 h-5 text-primary-600 mt-1 flex-shrink-0" />
                <span>Build active citizen engagement in governance processes</span>
              </li>
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="bg-gradient-to-br from-primary-50 to-secondary-50 p-8 rounded-2xl"
          >
            <div className="flex items-center space-x-3 mb-6">
              <Lightbulb className="w-8 h-8 text-secondary-600" />
              <h2 className="text-3xl font-bold text-gray-900">Our Vision</h2>
            </div>
            <p className="text-lg text-gray-700 leading-relaxed">
              A future where democracy thrives through transparency, where every citizen
              is informed and engaged, and where institutions serve the public interest
              with complete openness and accountability.
            </p>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="text-center bg-gray-50 rounded-2xl p-12"
        >
          <Heart className="w-12 h-12 text-red-500 mx-auto mb-6" />
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Values</h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Transparency</h3>
              <p className="text-gray-600">Open access to information and decision-making processes</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Accountability</h3>
              <p className="text-gray-600">Holding institutions responsible for their actions</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Empowerment</h3>
              <p className="text-gray-600">Giving citizens the tools to make a difference</p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default MissionPage;
